import AppLayout from "components/Layout/AppLayout";
import React, { useEffect, useState, useRef } from "react";
import SearchHeader from "./components/SearchHeader";
import { ReactComponent as MapIcon } from "assets/icons/map.svg";
import { ReactComponent as ListIcon } from "assets/icons/list_2.svg";
import useProperties from "hooks/useProperties";
import { createFiltersFromUrl } from "./filters";
import { useInView } from "react-intersection-observer";
import SearchPropertiesMap from "components/Map/SearchPropertiesMap";
import useBookmark from "hooks/useBookmark";
import { useLocation } from "react-router";
import useAuth from "hooks/useAuth";
import PropertyPopup from "components/Dialog/PropertyPopup";
import RemoveWarningDialog from "components/Dialog/RemoveWarningDialog";
import SearchResultCard from "components/Card/SearchResultCard";
import { formatNumericNumber } from "utils/formatNumber";
import { formatLocationName } from "utils/commonUtils";
import useSearch from "hooks/useSearch";
import SearchResultPropertyCardSkeleton from "components/Skeleton/SearchResultPropertyCardSkeleton";
import SearchMobileFilter from "./components/SearchMobileFilter";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import {
  storeInIndexedDB,
  getFromIndexedDB,
  clearIndexedDB,
} from "utils/indexedDBService";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ReactComponent as InfoIcon } from "assets/icons/info_2.svg";

const Research = () => {
  const {
    properties,
    loading,
    paginationLoading,
    error,
    searchProperties,
    searchMapProperties,
    isMapVisible,
    setIsMapVisible,
    setSelectedPropertyPopup,
    selectedPropertyPopup,
    fetchDoubtProperties,
    fetchDiscardedProperties,
    setProperties,
  } = useProperties();
  const { loadBookmarks } = useBookmark();
  const { user } = useAuth();
  const { selectedLocations } = useSearch();
  const isMobile = useMediaQuery({ query: "(max-width: 640px)" });
  const hasHandledCache = useRef(false);

  const location = useLocation();
  const scrollableContainerRef = useRef(null);

  // Search properties on refresh
  useEffect(() => {
    const loadProperties = async () => {
      const isReturningFromDetails = sessionStorage.getItem(
        "returningFromDetails"
      );

      // Run this only if the cache has not been handled yet
      if (!hasHandledCache.current) {
        try {
          if (isReturningFromDetails === "true") {
            const cachedProperties = await getFromIndexedDB();
            if (cachedProperties) {
              setProperties(cachedProperties);
              await clearIndexedDB(); // Clear cache after setting properties
              sessionStorage.removeItem("returningFromDetails");
            }
          } else {
            // Fetch new data if no cached data or not returning from details
            const filters = createFiltersFromUrl(window.location.href);
            searchProperties(filters);
            searchMapProperties(filters);
          }
        } catch (error) {
          console.error("Error handling property cache:", error);
          // Fallback to fetching new data
          const filters = createFiltersFromUrl(window.location.href);
          searchProperties(filters);
          searchMapProperties(filters);
        }
        // Mark cache as handled to prevent multiple runs
        hasHandledCache.current = true;
      }
    };

    loadProperties();
  }, [location.search]);

  // Reset the cache handler more safely when URL changes (not on each mount)
  useEffect(() => {
    const isReturningFromDetails = sessionStorage.getItem(
      "returningFromDetails"
    );
    if (!isReturningFromDetails) {
      // Only reset if we're not flagged as returning from details
      hasHandledCache.current = false;
    }
  }, [location]);

  // Store properties in IndexedDB when they change
  useEffect(() => {
    const storeProperties = async () => {
      if (properties.results && properties.results.length > 0) {
        try {
          await storeInIndexedDB(properties);
        } catch (error) {
          console.error("Error storing properties in IndexedDB:", error);
        }
      }
    };

    storeProperties();
  }, [properties]);

  // Restore scroll position when returning to the page
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem(
      "researchScrollPosition"
    );
    if (savedScrollPosition && scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = parseInt(savedScrollPosition);
      sessionStorage.removeItem("researchScrollPosition");
    }
  }, [properties.results]);

  // Fetch doubts, bookmarks and discarded property list
  useEffect(() => {
    if (user) {
      fetchDoubtProperties();
      fetchDiscardedProperties();
      loadBookmarks();
    }
  }, [user]);

  // Property pagination
  const { ref: lastElementRef, inView } = useInView({
    threshold: 1.0,
    triggerOnce: false,
    rootMargin: "0px 0px 500px 0px",
  });

  useEffect(() => {
    if (inView && properties.next && !loading && !paginationLoading) {
      const filters = createFiltersFromUrl(window.location.href);
      searchProperties(filters, properties.next);
    }
  }, [inView, loading, paginationLoading, properties.next, location]);

  return (
    <AppLayout isSearchScreen={true} isSearchVisable={false}>
      <div
        style={{ position: selectedPropertyPopup ? "relative" : "initial" }}
        className="bg-white sm:h-[calc(100vh-88px)] pb-12 sm:pb-0"
      >
        {isMobile && isMapVisible ? (
          <SearchPropertiesMap />
        ) : (
          <>
            <div className="px-5 mt-4 block sm:hidden">
              <SearchMobileFilter />
            </div>
            <div className="overflow-x-hidden">
              <SearchHeader />
              <div className="flex items-start  px-5 sm:px-0 justify-center">
                {!isMobile && <SearchPropertiesMap />}
                <div
                  id="research-scrollable-container"
                  ref={scrollableContainerRef}
                  className="sm:overflow-y-auto sm:px-6 w-full max-w-[400px] sm:max-w-[360px] lg:max-w-[700px] xl:max-w-[800px] sm:h-[calc(100vh-164px)] pb-[32px]"
                >
                  <div className="mt-5 mb-4 hidden sm:block">
                    <h1 className="text-[18px] font-normal leading-[20px] text-black">
                      Ricerca Proprietà -{" "}
                      {selectedLocations.length > 0 // If any selected location
                        ? selectedLocations.length > 1 // If selected location more than 1
                          ? `Milano • ${selectedLocations.length} zone selezionate`
                          : formatLocationName(selectedLocations[0]?.name)
                        : "Milano"}
                    </h1>
                    <div className="flex items-center mt-2">
                      <p className="text-[14px] leading-[20px] font-semibold text-[#00000080] mr-2">
                        {!loading
                          ? formatNumericNumber(properties.count || 0)
                          : 0}{" "}
                        Risultati
                      </p>
                      <span
                        className="cursor-pointer "
                        data-tooltip-id="search_result_tooltip"
                        data-tooltip-content="Su molti siti immobiliari sono presenti doppioni degli stessi annunci per aumentare artificialmente il numero totale degli Immobili. HouseScan si assicura di eliminare questi annunci doppiati!"
                      >
                        <InfoIcon className="w-[14px] h-[14px]" />
                      </span>
                      {/* Info Tooltip  */}
                      <ReactTooltip
                        id="search_result_tooltip"
                        place="bottom"
                        positionStrategy="fixed"
                        className="!max-w-64 !z-10 !text-sm !text-gray-500 !bg-white border !border-gray-200 !rounded-lg !shadow-xs"
                      />
                    </div>
                  </div>

                  <div className="grid gap-[20px] w-full lg:grid-cols-2">
                    {!loading &&
                      properties?.results?.length > 0 &&
                      properties.results.map((property, index) => (
                        <SearchResultCard
                          key={property._id}
                          property={property}
                          lastPropertyRef={
                            index === properties.results.length - 1
                              ? lastElementRef
                              : null
                          }
                        />
                      ))}

                    {(loading || paginationLoading) &&
                      Array.from({ length: 6 }).map((_, ind) => (
                        <SearchResultPropertyCardSkeleton key={ind} />
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Property card on map click  */}
        <PropertyPopup
          popupType="mapProperty"
          backdropStyle={{ position: "absolute" }}
        />
        {/* Mobile Map and List floting button  */}
        <div className="h-10 mobile-flot-button-shadow rounded-[8px] overflow-hidden sm:hidden bg-white inline-flex items-center w-[189px] z-[10] fixed bottom-[94px] left-1/2 -translate-x-1/2">
          <motion.div
            onClick={() => setIsMapVisible(true)}
            animate={{ backgroundColor: isMapVisible ? "#1DA1F2" : "#FFFFFF" }}
            transition={{ duration: 0.3 }}
            className="flex items-center space-x-1 h-full w-1/2 justify-center cursor-pointer"
          >
            <MapIcon style={{ fill: isMapVisible ? "#FFFFFF" : "#000000" }} />
            <motion.p
              animate={{ color: isMapVisible ? "#FFFFFF" : "#000000" }}
              transition={{ duration: 0.3 }}
              className="text-[14px] font-semibold leading-[20px]"
            >
              Map
            </motion.p>
          </motion.div>

          <motion.div
            onClick={() => setIsMapVisible(false)}
            animate={{ backgroundColor: !isMapVisible ? "#1DA1F2" : "#FFFFFF" }}
            transition={{ duration: 0.3 }}
            className="flex items-center space-x-1 h-full w-1/2 justify-center cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center ">
              <ListIcon
                style={{ fill: !isMapVisible ? "#FFFFFF" : "#000000" }}
              />
            </span>
            <motion.p
              animate={{ color: !isMapVisible ? "#FFFFFF" : "#000000" }}
              transition={{ duration: 0.3 }}
              className="text-[14px] font-semibold leading-[20px]"
            >
              List
            </motion.p>
          </motion.div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Research;
